import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { langPath } from '../../utils/langPath';
import { siteContent } from '../../data/siteContent';
import CGUModal from '../ui/CGUModal';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [showCGU, setShowCGU] = useState(false);

  const isDashboard = location.pathname.startsWith('/dashboard');
  const isLanding = !isDashboard;

  if (isDashboard) return null;

  const footerLinks = language === 'fr' ? siteContent.navigation.footerFr : siteContent.navigation.footerEn;

  const handleLinkClick = (link) => {
    if (link.href === '/cgu') {
      setShowCGU(true);
    } else {
      // siteContent.navigation.footerFr/En stocke des paths bruts sans préfixe lang
      // (ex. "/blog", "/about") — on les préfixe ici avec /fr ou /en selon la langue active.
      navigate(langPath(link.href, language));
      window.scrollTo(0, 0);
    }
  };

  // Landing footer — minimal dark style
  if (isLanding) {
    // Group links into columns of 2
    const col1 = footerLinks.slice(0, 2);
    const col2 = footerLinks.slice(2, 4);
    const col3 = footerLinks.slice(4);

    return (
      <>
        {showCGU && <CGUModal onClose={() => setShowCGU(false)} />}
        <footer className="w-full bg-[#0f0f0f] text-white border-t border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              {/* Left — link columns */}
              <div className="flex gap-12 md:gap-16">
                {[col1, col2, col3].map((col, ci) => (
                  col.length > 0 && (
                    <div key={ci} className="flex flex-col gap-2">
                      {col.map((link, i) => (
                        <button
                          key={i}
                          onClick={() => handleLinkClick(link)}
                          className="text-sm text-white/50 hover:text-white transition text-left"
                        >
                          {link.label}
                        </button>
                      ))}
                    </div>
                  )
                ))}
              </div>

              {/* Right — copyright + logo */}
              <div className="flex items-center gap-6">
                <p className="text-xs text-white/30">&copy; 2014 - 2026 nizus</p>
                <button onClick={() => { navigate(langPath('/', language)); window.scrollTo(0, 0); }}>
                  <img src="/img/logo-nizus.svg" alt="Nizus" className="h-5 w-auto" />
                </button>
              </div>
            </div>
          </div>
        </footer>
      </>
    );
  }

  // Default footer for other pages
  return (
    <>
    {showCGU && <CGUModal onClose={() => setShowCGU(false)} />}
    <footer className="w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-500">
      {/* CTA Section */}
      {!user && (
        <div className="w-full py-16 flex flex-col items-center justify-center border-b border-gray-200 dark:border-white/10">
          <h2 className="text-3xl lg:text-4xl font-bold mb-8 text-gray-900 dark:text-white">
            {language === 'fr' ? 'Rejoignez la communauté' : 'Join the community'}
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <button
              className="px-6 py-3 border-2 border-green-500 dark:border-green-400 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/10 transition rounded-full font-semibold"
              onClick={() => { navigate(langPath('/signup', language)); window.scrollTo(0, 0); }}
            >
              {language === 'fr' ? 'Essai gratuit' : 'Free Trial'}
            </button>
            <button
              className="px-6 py-3 bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-white/20 transition rounded-full font-semibold"
              onClick={() => { navigate(langPath('/login', language)); window.scrollTo(0, 0); }}
            >
              {language === 'fr' ? 'Connexion' : 'Log In'}
            </button>
            <button
              className="px-6 py-3 bg-green-500 text-white hover:bg-green-600 transition rounded-full font-semibold shadow-lg shadow-green-500/25"
              onClick={() => { navigate(langPath('/signup', language)); window.scrollTo(0, 0); }}
            >
              {language === 'fr' ? 'Inscription' : 'Sign Up'}
            </button>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="w-full bg-gray-200 dark:bg-gray-950 py-8 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-8">
          <button className="flex-shrink-0" onClick={() => navigate(langPath('/', language))} aria-label="Go to home">
            <img src="/img/logo-nizus.svg" alt="NIZUS Logo" className="h-10 w-auto invert dark:invert-0" />
          </button>

          <nav className="flex flex-wrap justify-center gap-6 text-sm">
            {footerLinks.map((link, index) => (
              <button
                key={index}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition font-medium"
                onClick={() => handleLinkClick(link)}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex-shrink-0">
            <img src="/img/payment.png" alt="Payment methods" className="h-8 w-auto" />
          </div>
        </div>

        <div className="text-center text-gray-500 text-xs mt-6 border-t border-gray-300 dark:border-gray-800 pt-6">
          <p>&copy; 2014 - 2026 nizus</p>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
